import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Edit, Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { formatMobileHeaderDate } from "../utils/attendanceHelpers";

// ============================================================================
// Types
// ============================================================================

interface DialogHeaderSectionProps {
  selectedLesson: any;
  isMobile: boolean;
  badgeCounts: { present: number; absent: number; pending: number } | null;
  recordedMeta: { name: string; atISO: string } | null;
  attendanceViewMode: "overview" | "edit";
  overviewData: any;
  canEditExisting: boolean;
  onSwitchToEdit: () => void;
  /** When true, hides badges and edit button (future lesson preview) */
  isFuturePreview?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function formatRecordedTimestamp(
  meta: { name: string; atISO: string },
  includeUhr = true,
) {
  const d = new Date(/Z$/.test(meta.atISO) ? meta.atISO : meta.atISO + "Z");
  const date = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
  const time = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
  return `Erfasst von ${meta.name} am ${date} um ${time}${
    includeUhr ? " Uhr" : ""
  }`;
}

// ============================================================================
// Shared sub-sections
// ============================================================================

function BadgeCounts({
  counts,
  justify = "center",
  isMobile = false,
}: {
  counts: { present: number; absent: number; pending: number };
  justify?: "center" | "start";
  isMobile?: boolean;
}) {
  // Mobile: compact labels, semantic colours, hide Ausstehend when zero.
  if (isMobile) {
    return (
      <div
        className={`mt-1 flex items-center justify-${justify} gap-1 flex-wrap`}
      >
        <Badge
          variant="outline"
          className="text-xs bg-green-50 text-green-700 border-green-200"
        >
          Anw: {counts.present}
        </Badge>
        <Badge
          variant="outline"
          className="text-xs bg-red-50 text-red-700 border-red-200"
        >
          Abw: {counts.absent}
        </Badge>
        {counts.pending > 0 && (
          <Badge
            variant="outline"
            className="text-xs bg-orange-50 text-orange-700 border-orange-200"
          >
            Ausstehend: {counts.pending}
          </Badge>
        )}
      </div>
    );
  }
  return (
    <div
      className={`mt-1 flex items-center justify-${justify} gap-2 flex-wrap`}
    >
      <Badge variant="secondary" className="text-xs">
        Anwesend: {counts.present}
      </Badge>
      <Badge variant="destructive" className="text-xs">
        Abwesend: {counts.absent}
      </Badge>
      <Badge variant="outline" className="text-xs">
        Ausstehend: {counts.pending}
      </Badge>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function DialogHeaderSection({
  selectedLesson,
  isMobile,
  badgeCounts,
  recordedMeta,
  attendanceViewMode,
  overviewData,
  canEditExisting,
  onSwitchToEdit,
  isFuturePreview = false,
}: DialogHeaderSectionProps) {
  return (
    <>
      <div className="text-center lg:text-left overflow-hidden">
        {isMobile ? (
          <>
            <div className="text-base lg:text-xl font-semibold leading-tight break-words">
              {selectedLesson?.subject} {selectedLesson?.class}
            </div>
            <div className="text-sm font-normal text-gray-600 mt-1">
              {formatMobileHeaderDate(
                selectedLesson?.start_datetime
                  ? new Date(selectedLesson.start_datetime)
                  : new Date(),
                selectedLesson?.time,
              )}
            </div>
            {/* Badges only show once the lesson has been confirmed —
                before that they would read "Anw: 0 / Abw: 0 / Ausstehend:
                N" and just clutter the header. The prefill summary card
                in the body covers the not-yet-confirmed state. */}
            {!isFuturePreview && badgeCounts && recordedMeta && (
              <BadgeCounts
                counts={badgeCounts}
                justify="center"
                isMobile={isMobile}
              />
            )}
            {!isFuturePreview && recordedMeta && (
              <div className="text-xs text-muted-foreground mt-1 break-words">
                {formatRecordedTimestamp(recordedMeta, false)}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-lg lg:text-xl font-semibold">
              Anwesenheit - {selectedLesson?.subject} {selectedLesson?.class}
            </div>
            <div className="text-sm font-normal text-gray-600 mt-1">
              {(() => {
                const d = selectedLesson?.start_datetime
                  ? new Date(selectedLesson.start_datetime)
                  : new Date();
                if (isNaN(d.getTime()))
                  return selectedLesson?.time
                    ? `${selectedLesson.time} Uhr`
                    : "Datum unbekannt";
                return `${d.toLocaleDateString("de-DE", {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })} um ${selectedLesson?.time} Uhr`;
              })()}
            </div>
            {/* See mobile branch above — badges suppressed until confirm. */}
            {!isFuturePreview && badgeCounts && recordedMeta && (
              <BadgeCounts
                counts={badgeCounts}
                justify="start"
                isMobile={isMobile}
              />
            )}
            {!isFuturePreview && recordedMeta && (
              <div className="text-xs text-muted-foreground mt-1">
                {formatRecordedTimestamp(recordedMeta)}
              </div>
            )}
          </>
        )}
      </div>
      {!isFuturePreview && attendanceViewMode === "overview" && overviewData && canEditExisting && (() => {
        const isKbLocked = !!(selectedLesson as any)?.kb_locked;
        const kbApprovedByName = (selectedLesson as any)?.kb_approved_by_name as
          | string
          | null
          | undefined;
        const kbApprovedAt = (selectedLesson as any)?.kb_approved_at as
          | string
          | null
          | undefined;
        const kbLockTooltip = isKbLocked
          ? `Geprüft${kbApprovedByName ? ` von ${kbApprovedByName}` : ""}${
              kbApprovedAt
                ? ` am ${new Intl.DateTimeFormat("de-DE", {
                    timeZone: "Europe/Berlin",
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }).format(new Date(kbApprovedAt))}`
                : ""
            }`
          : "";
        return (
          <div className="flex justify-center lg:justify-end">
            {isKbLocked ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Lock
                      className="size-5 text-emerald-600 cursor-help"
                      aria-label={kbLockTooltip}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    Bearbeitung nicht möglich — {kbLockTooltip}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={onSwitchToEdit}
                className="flex items-center gap-1"
              >
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Anwesenheit bearbeiten</span>
                <span className="sm:hidden">Bearbeiten</span>
              </Button>
            )}
          </div>
        );
      })()}
    </>
  );
}
